/**
 * Answers travel between Word and mytender as markdown, the format the web
 * app's converters already speak (markdown_to_plate / plate_to_text). This is
 * the pane's side: a small markdown reader for what those converters produce
 * (headings, paragraphs, bulleted and numbered lists, tables, bold, italic,
 * links), and a writer that turns the reader's blocks back into markdown.
 */

export type Inline = { text: string; bold?: boolean; italic?: boolean; href?: string };

export type Block =
  | { kind: "heading"; level: number; inlines: Inline[] }
  | { kind: "paragraph"; inlines: Inline[] }
  | { kind: "bullet"; depth: number; inlines: Inline[] }
  | { kind: "number"; depth: number; ordinal: number; inlines: Inline[] }
  | { kind: "table"; rows: Inline[][][]; header: boolean }
  /** A line holding only --- (or *** / ___): a page break in Word. */
  | { kind: "pagebreak" };

const LINK = /\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
/** Link placeholders while emphasis is parsed: <n>. */
const LINK_TOKEN = /(\d+)/g;

/**
 * Parse inline markdown: **bold**, *italic* / _italic_, `code`, [text](url).
 * Links are swapped for placeholders first so emphasis can wrap them
 * ("**see [policy](url)**"), then expanded with the surrounding formatting.
 */
export const parseInline = (source: string): Inline[] => {
  const links: { text: string; href: string }[] = [];
  const tokenised = source.replace(LINK, (_m, text: string, href: string) => {
    links.push({ text, href });
    return `${links.length - 1}`;
  });
  const out: Inline[] = [];
  for (const inline of parseEmphasis(tokenised)) {
    let last = 0;
    for (const match of inline.text.matchAll(LINK_TOKEN)) {
      const at = match.index ?? 0;
      if (at > last) out.push({ ...inline, text: inline.text.slice(last, at) });
      const link = links[Number(match[1])];
      for (const inner of parseEmphasis(link.text))
        out.push({
          ...inner,
          ...(inline.bold && { bold: true }),
          ...(inline.italic && { italic: true }),
          href: link.href,
        });
      last = at + match[0].length;
    }
    if (last < inline.text.length) out.push({ ...inline, text: inline.text.slice(last) });
  }
  return out.filter((i) => i.text.length > 0);
};

/**
 * Emphasis. "*" works anywhere; "_" only at word edges, as in CommonMark, so
 * file_name_here stays plain.
 */
const parseEmphasis = (source: string): Inline[] => {
  const out: Inline[] = [];
  let text = source.replace(/`([^`]+)`/g, "$1");
  text = text.replace(/\\([*_`[\]\\|#])/g, "$1");
  const pattern =
    /(\*\*\*)(?!\s)(.+?)(?<!\s)\*\*\*|(?<![\p{L}\p{N}])___(?!\s)(.+?)(?<!\s)___(?![\p{L}\p{N}])|(\*\*)(?!\s)(.+?)(?<!\s)\*\*|(?<![\p{L}\p{N}])__(?!\s)(.+?)(?<!\s)__(?![\p{L}\p{N}])|\*(?![\s*])(.+?)(?<![\s*])\*(?!\*)|(?<![\p{L}\p{N}])_(?![\s_])(.+?)(?<![\s_])_(?![\p{L}\p{N}])/gu;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text))) {
    if (match.index > last) out.push({ text: text.slice(last, match.index) });
    const boldItalic = match[2] ?? match[3];
    const bold = match[5] ?? match[6];
    const italic = match[7] ?? match[8];
    // Emphasis nests ("**bold *and italic***"): parse the inside too.
    const inner = (content: string, flags: Pick<Inline, "bold" | "italic">) =>
      parseEmphasis(content).map((i) => ({
        ...i,
        ...(flags.bold && { bold: true }),
        ...(flags.italic && { italic: true }),
      }));
    if (boldItalic !== undefined) out.push(...inner(boldItalic, { bold: true, italic: true }));
    else if (bold !== undefined) out.push(...inner(bold, { bold: true }));
    else out.push(...inner(italic ?? "", { italic: true }));
    last = match.index + match[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out.map((i) => ({ ...i, text: i.text.replace(/(.)/g, "$1") })).filter((i) => i.text.length > 0);
};

const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\\\|/g, "|"));

/** Parse markdown into blocks. */
export const parseMarkdown = (markdown: string): Block[] => {
  const lines = (markdown ?? "").replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length) blocks.push({ kind: "paragraph", inlines: parseInline(paragraph.join(" ").trim()) });
    paragraph = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.trim()) {
      flush();
      continue;
    }
    // A rule on its own line is a page break; one right under a table row is
    // that table's separator and never reaches here.
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim()) && !(i > 0 && lines[i - 1].includes("|"))) {
      flush();
      blocks.push({ kind: "pagebreak" });
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(line.trim());
    if (heading) {
      flush();
      blocks.push({
        kind: "heading",
        level: heading[1].length,
        inlines: parseInline(heading[2].replace(/\s+#+\s*$/, "")),
      });
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1])) {
      flush();
      const rows: Inline[][][] = [splitRow(line).map(parseInline)];
      i += 2;
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i += 1;
      }
      i -= 1;
      blocks.push({ kind: "table", rows, header: true });
      continue;
    }
    const bullet = /^(\s*)[-*+•]\s+(.*)$/.exec(line);
    if (bullet) {
      flush();
      blocks.push({
        kind: "bullet",
        depth: Math.floor(bullet[1].replace(/\t/g, "  ").length / 2),
        inlines: parseInline(bullet[2]),
      });
      continue;
    }
    const numbered = /^(\s*)(\d+)[.)]\s+(.*)$/.exec(line);
    if (numbered) {
      flush();
      blocks.push({
        kind: "number",
        depth: Math.floor(numbered[1].replace(/\t/g, "  ").length / 2),
        ordinal: Number(numbered[2]),
        inlines: parseInline(numbered[3]),
      });
      continue;
    }
    paragraph.push(line.trim().replace(/^>\s?/, ""));
  }
  flush();
  return blocks.filter((b) => b.kind === "table" || b.kind === "pagebreak" || b.inlines.length > 0);
};

const escapeInline = (text: string): string => text.replace(/([*_`\\|])/g, "\\$1");

/** Neighbouring inlines with the same formatting and link, joined. */
const mergeInlines = (inlines: Inline[]): Inline[] => {
  const out: Inline[] = [];
  for (const i of inlines) {
    const prev = out[out.length - 1];
    if (prev && !!prev.bold === !!i.bold && !!prev.italic === !!i.italic && prev.href === i.href) prev.text += i.text;
    else out.push({ ...i });
  }
  return out;
};

export const inlinesToMarkdown = (inlines: Inline[]): string =>
  mergeInlines(inlines)
    .map((i) => {
      const t = escapeInline(i.text);
      if (!t.trim()) return t;
      const lead = /^\s*/.exec(t)?.[0] ?? "";
      const trail = /\s*$/.exec(t)?.[0] ?? "";
      let core = t.trim();
      if (i.bold && i.italic) core = `***${core}***`;
      else if (i.bold) core = `**${core}**`;
      else if (i.italic) core = `*${core}*`;
      if (i.href) core = `[${core}](${i.href})`;
      return `${lead}${core}${trail}`;
    })
    .join("");

/** Blocks back to markdown, the form mytender's converter reads. */
export const blocksToMarkdown = (blocks: Block[]): string => {
  const parts: string[] = [];
  let listRun: string[] = [];
  let listKind: "bullet" | "number" | null = null;
  const flushList = () => {
    if (listRun.length) parts.push(listRun.join("\n"));
    listRun = [];
    listKind = null;
  };
  for (const block of blocks) {
    // A top-level bulleted list straight after a numbered one (or the
    // reverse) needs a blank line between them to stay two lists; a nested
    // list of the other kind stays inside its parent.
    const listItem = block.kind === "bullet" || block.kind === "number";
    if (listItem && block.depth === 0 && listKind && listKind !== block.kind) flushList();
    if (listItem && block.depth === 0) listKind = block.kind as "bullet" | "number";
    switch (block.kind) {
      case "bullet":
        listRun.push(`${"  ".repeat(block.depth)}- ${inlinesToMarkdown(block.inlines)}`);
        continue;
      case "number":
        listRun.push(`${"  ".repeat(block.depth)}${block.ordinal}. ${inlinesToMarkdown(block.inlines)}`);
        continue;
      default:
        flushList();
    }
    if (block.kind === "heading") parts.push(`${"#".repeat(block.level)} ${inlinesToMarkdown(block.inlines)}`);
    else if (block.kind === "pagebreak") parts.push("---");
    else if (block.kind === "paragraph") parts.push(inlinesToMarkdown(block.inlines));
    else if (block.kind === "table" && block.rows.length) {
      const width = Math.max(...block.rows.map((r) => r.length));
      const row = (cells: Inline[][]) =>
        `| ${Array.from({ length: width }, (_, c) => inlinesToMarkdown(cells[c] ?? []).replace(/\n/g, " ")).join(" | ")} |`;
      parts.push(
        [row(block.rows[0]), `| ${Array(width).fill("---").join(" | ")} |`, ...block.rows.slice(1).map(row)].join("\n")
      );
    }
  }
  flushList();
  return parts
    .filter((p) => p.trim())
    .join("\n\n")
    .trim();
};

/**
 * The same fingerprint the backend computes (sha256 of whitespace-collapsed
 * markdown, first 16 hex chars), so "unchanged" means the same on both sides.
 */
export const contentHash = async (markdown: string): Promise<string> => {
  const normalised = (markdown ?? "").replace(/\s+/g, " ").trim();
  const bytes = new TextEncoder().encode(normalised);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
};
